import { create } from "zustand";
import {
  DEFAULT_DARK_THEME_ID,
  DEFAULT_LIGHT_THEME_ID,
  selectTheme,
  themeCssVariables,
  type AppearancePreference,
  type ResolvedTheme,
  type ThemePreference,
} from "@rivets/core/themes";
import { BUILT_IN_THEMES } from "@rivets/core/themes-builtin";

/**
 * The app's colour theme — a device preference, like the native apps' own
 * Appearance setting, so it lives in this browser rather than on the account.
 *
 * Appearance picks dark, light or the system's; each appearance then has its
 * own theme, the way Zed pairs a dark and a light theme. Applying one sets the
 * tokens as inline custom properties on `<html>`, which outrank every rule in
 * `global.css`, and `data-theme` for the few rules that key on the appearance
 * itself (shadows, the select chevron).
 *
 * The first paint cannot wait for this module: `public/theme-boot.js`, loaded
 * from `index.html`, replays the last applied variables from `BOOT_STORAGE_KEY`, so a
 * light theme never flashes the dark defaults in `global.css`.
 */

export const THEME_PREFERENCE_STORAGE_KEY = "rivets.theme.v1";
/** Read by `public/theme-boot.js` — keep the two in step. */
export const BOOT_STORAGE_KEY = "rivets.theme-boot.v1";

export const DEFAULT_THEME_PREFERENCE: ThemePreference = {
  appearance: "dark",
  darkThemeId: DEFAULT_DARK_THEME_ID,
  lightThemeId: DEFAULT_LIGHT_THEME_ID,
};

export type ThemeStorage = Pick<Storage, "getItem" | "setItem">;

export function readThemePreference(storage: ThemeStorage | undefined): ThemePreference {
  try {
    const raw = storage?.getItem(THEME_PREFERENCE_STORAGE_KEY);
    if (!raw) return DEFAULT_THEME_PREFERENCE;
    const parsed = JSON.parse(raw) as Partial<ThemePreference>;
    return {
      appearance: parsed.appearance === "light" || parsed.appearance === "auto" ? parsed.appearance : "dark",
      darkThemeId: typeof parsed.darkThemeId === "string" ? parsed.darkThemeId : DEFAULT_DARK_THEME_ID,
      lightThemeId: typeof parsed.lightThemeId === "string" ? parsed.lightThemeId : DEFAULT_LIGHT_THEME_ID,
    };
  } catch {
    return DEFAULT_THEME_PREFERENCE;
  }
}

/** What the boot script needs to repaint without this module: the variables
 * for both appearances, and which one to use. */
export type ThemeBootRecord = {
  appearance: AppearancePreference;
  dark: Record<string, string>;
  light: Record<string, string>;
};

export function bootRecord(preference: ThemePreference, themes: readonly ResolvedTheme[] = BUILT_IN_THEMES): ThemeBootRecord {
  return {
    appearance: preference.appearance,
    dark: themeCssVariables(selectTheme(themes, preference, true)),
    light: themeCssVariables(selectTheme(themes, preference, false)),
  };
}

function writeThemePreference(storage: ThemeStorage | undefined, preference: ThemePreference): void {
  try {
    storage?.setItem(THEME_PREFERENCE_STORAGE_KEY, JSON.stringify(preference));
    storage?.setItem(BOOT_STORAGE_KEY, JSON.stringify(bootRecord(preference)));
  } catch {
    /* Private mode or a full quota: the in-memory preference still applies. */
  }
}

function browserStorage(): ThemeStorage | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

const systemDarkQuery = typeof window !== "undefined" && typeof window.matchMedia === "function"
  ? window.matchMedia("(prefers-color-scheme: dark)")
  : null;

/** Paints `theme` onto the document root. */
export function applyThemeToDocument(theme: ResolvedTheme, root: HTMLElement = document.documentElement): void {
  for (const [name, value] of Object.entries(themeCssVariables(theme))) root.style.setProperty(name, value);
  root.style.colorScheme = theme.appearance;
  root.dataset.theme = theme.appearance;
  root.dataset.themeId = theme.id;
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (meta) meta.content = theme.colors["surface-0"];
}

interface ThemeState {
  preference: ThemePreference;
  systemPrefersDark: boolean;
  /** The theme currently painted. */
  active: ResolvedTheme;
  setAppearance: (appearance: AppearancePreference) => void;
  setThemeId: (appearance: "dark" | "light", id: string) => void;
}

function resolveActive(preference: ThemePreference, systemPrefersDark: boolean): ResolvedTheme {
  return selectTheme(BUILT_IN_THEMES, preference, systemPrefersDark);
}

export const useThemeStore = create<ThemeState>((set, get) => {
  const preference = readThemePreference(browserStorage());
  const systemPrefersDark = systemDarkQuery?.matches ?? true;
  const update = (next: ThemePreference) => {
    writeThemePreference(browserStorage(), next);
    set({ preference: next, active: resolveActive(next, get().systemPrefersDark) });
  };
  return {
    preference,
    systemPrefersDark,
    active: resolveActive(preference, systemPrefersDark),
    setAppearance: (appearance) => update({ ...get().preference, appearance }),
    setThemeId: (appearance, id) => update({ ...get().preference, [appearance === "dark" ? "darkThemeId" : "lightThemeId"]: id }),
  };
});

/** Starts painting the active theme and keeps it painted: on every change of
 * preference, and of the system appearance while the preference is Auto. Called
 * once, before the first render. */
export function installTheme(): void {
  applyThemeToDocument(useThemeStore.getState().active);
  // Rewritten on every load, so a boot record from an older build — whose
  // variables no longer match the built-in themes — is replaced at once.
  writeThemePreference(browserStorage(), useThemeStore.getState().preference);
  useThemeStore.subscribe((state, previous) => {
    if (state.active !== previous.active) applyThemeToDocument(state.active);
  });
  systemDarkQuery?.addEventListener("change", (event) => {
    useThemeStore.setState((state) => ({
      systemPrefersDark: event.matches,
      active: resolveActive(state.preference, event.matches),
    }));
  });
}

export function useActiveTheme(): ResolvedTheme {
  return useThemeStore((state) => state.active);
}

export { BUILT_IN_THEMES };
